  // client/src/components/admin/UserPermissionsModal.jsx
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  Route as RouteIcon,
  Schema as OntologyIcon,
  Dashboard as PanelIcon,
  SportsEsports as GameActionIcon,
  CheckCircle as CheckIcon,
  Cancel as CrossIcon,
} from '@mui/icons-material';
import Gateway from '../../api/gateway';
import RoleBadge from './RoleBadge';

// ============================================================================
// Конфигурация табов ресурсов
// ============================================================================

const RESOURCE_TABS = [
  { key: 'routes', label: 'Маршруты', icon: RouteIcon, type: 'route' },
  { key: 'ontologies', label: 'Онтологии', icon: OntologyIcon, type: 'ontology' },
  { key: 'admin_panels', label: 'Панели', icon: PanelIcon, type: 'admin_panel' },
  { key: 'game_actions', label: 'Действия', icon: GameActionIcon, type: 'game_action' },
];

const EMPTY_RESOURCES = {
  routes: [],
  ontologies: [],
  admin_panels: [],
  game_actions: [],
};

// ============================================================================
// Компонент списка ресурсов
// ============================================================================

function ResourceList({ list }) {
  if (!Array.isArray(list) || list.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        Нет доступных ресурсов в этой категории.
      </Typography>
    );
  }

  const hasWildcard = list.includes('*');

  return (
    <List dense>
      {hasWildcard && (
        <ListItem>
          <ListItemIcon sx={{ minWidth: 32 }}>
            <CheckIcon color="success" fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip label="*" color="error" size="small" />
                <Typography variant="body2" fontWeight="medium">
                  Полный доступ ко всем элементам категории
                </Typography>
              </Box>
            }
          />
        </ListItem>
      )}
      {!hasWildcard &&
        list.map((item) => (
          <ListItem key={item}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <CheckIcon color="success" fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={<Typography variant="body2">{item}</Typography>}
            />
          </ListItem>
        ))}
    </List>
  );
}

// ============================================================================
// Основной компонент
// ============================================================================

/**
 * Модал просмотра прав пользователя.
 *
 * @param {object} props
 * @param {object} props.user — объект из /api/access/users
 * @param {() => void} props.onClose
 */
export default function UserPermissionsModal({ user, onClose }) {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!user?.kratos_identity_id) {
        setError('Не удалось определить kratos_identity_id пользователя');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const res = await Gateway.api.accessAdmin.getUserPermissions(
          user.kratos_identity_id
        );
        if (cancelled) return;

        if (res?.success && res?.data) {
          setAccess(res.data);
        } else {
          setError('Неожиданный ответ сервера');
        }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 404) {
          setError('Пользователь не найден');
        } else if (status === 403) {
          setError('Недостаточно прав для просмотра');
        } else {
          setError(err.response?.data?.error || err.message || 'Ошибка загрузки');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [user?.kratos_identity_id]);

  const resources = access?.resources || EMPTY_RESOURCES;
  const characters = Array.isArray(access?.characters) ? access.characters : [];
  const systemRole = access?.system_role || user?.system_role || 'user';

  const displayName =
    access?.display_name || user?.display_name || user?.email || 'Пользователь';

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="h6" component="span">
            Права доступа
          </Typography>
          <RoleBadge role={systemRole} />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {displayName}
          {access?.player_id && ` • player_id: ${access.player_id}`}
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
              gap: 2,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Загрузка прав…
            </Typography>
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && access && (
          <Box>
            {/* Сводка */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {access.email || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Kratos ID
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}
                  >
                    {access.kratos_identity_id || '—'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    system_role
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <RoleBadge role={systemRole} textMode="short" />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Персонажей
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {characters.length}
                  </Typography>
                </Box>
              </Box>
            </Paper>

            {/* Табы ресурсов */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {RESOURCE_TABS.map((t) => {
                  const Icon = t.icon;
                  const count = Array.isArray(resources[t.key])
                    ? resources[t.key].length
                    : 0;
                  return (
                    <Tab
                      key={t.key}
                      icon={<Icon fontSize="small" />}
                      iconPosition="start"
                      label={`${t.label} (${count})`}
                      sx={{ minHeight: 48, textTransform: 'none' }}
                    />
                  );
                })}
              </Tabs>
            </Box>

            {RESOURCE_TABS.map((t, idx) =>
              tab === idx ? (
                <Box key={t.key} sx={{ minHeight: 120 }}>
                  <ResourceList list={resources[t.key]} />
                </Box>
              ) : null
            )}

            {/* Персонажи */}
            {characters.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Персонажи ({characters.length})
                </Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {characters.map((c) => (
                      <Chip
                        key={c.character_id}
                        label={`${c.name || c.character_id?.slice(0, 8)} • ${c.status || '?'}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Paper>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}